import { useCallback } from 'react'
import { supabase } from '../supabase/client' // Adjust path if needed

export const useIprProgress = () => {
  const saveProgress = useCallback(async (userId, talentId, progress) => {
    try {
      const { data, error } = await supabase
        .from('ipr_profiles')
        .select('profileData')
        .eq('userId', userId)
        .single()
      if (error) throw error
      if (!data) throw new Error('Profile not found')
      const profileData = JSON.parse(data.profileData)
      if (!profileData.iprProgress) profileData.iprProgress = {}
      if (!profileData.createdIPRs) profileData.createdIPRs = {}
      profileData.iprProgress[talentId] = progress
      profileData.createdIPRs[talentId] = progress
      const updatedProfileData = JSON.stringify(profileData)
      const updatedAt = new Date().toISOString()
      const { error: updateError } = await supabase
        .from('ipr_profiles')
        .update({ profileData: updatedProfileData, updatedAt })
        .eq('userId', userId)
      if (updateError) throw updateError
      return { success: true, message: 'Progress saved successfully' }
    } catch (error) {
      console.error('Error saving IPR progress:', error)
      return { success: false, error: error.message }
    }
  }, [])

  const getProgress = useCallback(async (userId) => {
    try {
      const { data, error } = await supabase
        .from('ipr_profiles')
        .select('profileData')
        .eq('userId', userId)
        .single()
      if (error) throw error
      if (!data) throw new Error('Profile not found')
      const profileData = JSON.parse(data.profileData)
      console.log(`Получение прогресса ИПР для пользователя ${userId}`)
      return { success: true, progress: profileData.iprProgress || {} }
    } catch (error) {
      console.error(`Ошибка при получении профиля: ${error.message}`)
      return { success: false, error: error.message }
    }
  }, [])

  const fetchTalentInfo = async (talentId) => {
    try {
      const { data, error } = await supabase
        .from('ipr_business_talents')
        .select('*')
        .eq('Number', talentId)
        .single()
      if (error) throw error
      return data
    } catch (error) {
      console.error('Error fetching talent info:', error)
      throw error
    }
  }

  const saveIPR = async (userId, talentId, iprContent) => {
    try {
      const { data, error } = await supabase
        .from('ipr_profiles')
        .select('profileData')
        .eq('userId', userId)
        .single()
      if (error) throw error
      if (!data) throw new Error('Profile not found')
      const profileData = JSON.parse(data.profileData)
      if (!profileData.iprs) profileData.iprs = {}
      if (!profileData.createdIPRs) profileData.createdIPRs = {}
      profileData.iprs[talentId] = iprContent
      if (!profileData.createdIPRs[talentId]) profileData.createdIPRs[talentId] = []
      const updatedProfileData = JSON.stringify(profileData)
      const updatedAt = new Date().toISOString()
      const { error: updateError } = await supabase
        .from('ipr_profiles')
        .update({ profileData: updatedProfileData, updatedAt })
        .eq('userId', userId)
      if (updateError) throw updateError
      return { success: true }
    } catch (error) {
      console.error('Error saving IPR:', error)
      throw error
    }
  }

  const getOrCreateIPR = useCallback(async (userId, talentId, feedback?: any) => {
    try {
      const { data, error } = await supabase
        .from('ipr_profiles')
        .select('profileData')
        .eq('userId', userId)
        .single()
      if (error) throw error
      if (!data) throw new Error('Profile not found')
      const profileData = JSON.parse(data.profileData)
      const existingIPR =
        profileData.iprs && profileData.iprs[talentId] ? profileData.iprs[talentId] : null

      if (existingIPR && !feedback) {
        return { success: true, ipr: existingIPR, isNew: false }
      } else {
        const talent = await fetchTalentInfo(talentId)
        if (!talent) throw new Error('Talent not found')
        if (!feedback) throw new Error('Feedback is required to create IPR')
        const {
          data: { user },
          error: authError,
        } = await supabase.auth.getUser()
        if (authError || !user) {
          throw new Error('User not authenticated or session invalid')
        }
        try {
          const response = await supabase.functions.invoke('create-talent-ipr', {
            body: JSON.stringify({ talentInfo: talent, feedback }),
          })
          if (response.error) {
            console.error('Edge Function invocation failed:', response.error, {
              userId,
              talentId,
              feedback,
              errorCode: response.error.code,
            })
            throw new Error(
              `Edge Function error: ${response.error.message} - Check deployment and CORS`,
            )
          }
          const generatedIPR = response.data.ipr
          await saveIPR(userId, talentId, generatedIPR)
          return { success: true, ipr: generatedIPR, isNew: true }
        } catch (error) {
          console.error('Error in getOrCreateIPR:', error.message, {
            userId,
            talentId,
            feedback,
          })
          return { success: false, error: error.message }
        }
      }
    } catch (error) {
      console.error('Error in getOrCreateIPR:', error)
      return { success: false, error: error.message }
    }
  }, [])

  return { saveProgress, getProgress, getOrCreateIPR, saveIPR }
}
